import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Building2, Users, GraduationCap, Heart, Globe, Sparkles, MapPin, Award } from 'lucide-react'

const projects = [
  {
    title: 'Smart Card Application for Orphan Child',
    department: 'Women and Child Development Department',
    category: 'Social Welfare',
    icon: Heart,
    description: 'Smart card for orphans providing secure and easily accessible identification system. Enables efficient tracking, documentation, and support for welfare and development. Grants access to education, healthcare, and social welfare programs. Streamlines administrative processes, facilitates data management and analysis, promotes empowerment and inclusion.',
    color: '#ec4899',
  },
  {
    title: 'Mahaposh Webportal',
    department: 'Women and Child Development Department',
    category: 'Digital Platform',
    icon: Globe,
    description: 'Comprehensive web portal for Women and Child Development Department enabling efficient service delivery and program management.',
    color: '#6366f1',
  },
  {
    title: 'Stree Shakti Platform',
    category: 'Women Empowerment',
    icon: Users,
    department: 'Women and Child Development Department',
    description: '"Rising Women, Rising Nation" - EmpowerHer is dedicated to fostering personal and professional growth of women through a comprehensive platform. Provides supportive, inclusive, financial, and educational space for women from diverse backgrounds to access resources, tools, and network for empowerment and equality.',
    color: '#8b5cf6',
  },
  {
    title: 'One Stop Centre Digital Webportal',
    department: 'Women and Child Development Department',
    category: 'Digital Services',
    icon: Building2,
    description: 'Integrated digital platform for one-stop support services for women, providing access to multiple government schemes and assistance programs.',
    color: '#06b6d4',
  },
  {
    title: 'Eklavya Digital E-Learning Application & Studio',
    department: 'Women and Child Development Department',
    category: 'Education Technology',
    icon: GraduationCap,
    description: 'Digital e-learning platform and studio for educational content delivery, supporting online learning initiatives.',
    color: '#f59e0b',
  },
  {
    title: 'Yashwini Online Mahila Bachat Gat',
    department: 'Women and Child Development Department',
    category: 'E-Commerce Platform',
    icon: Globe,
    description: 'E-commerce platform for women self-help groups enabling online marketing and sales of products.',
    color: '#10b981',
  },
  {
    title: 'SJSA Online Hostel Management',
    department: 'Social Justice & Special Assistance Department',
    category: 'Management System',
    icon: Building2,
    description: 'Online hostel management application for students with complete application tracking and management system.',
    color: '#6366f1',
  },
  {
    title: 'Digital Website - Regional Deputy Commissioner',
    department: 'Regional Deputy Commissioner Office',
    category: 'Government Portal',
    icon: Globe,
    description: 'Official digital website for Regional Deputy Commissioner office, Konkan Bhavan.',
    color: '#8b5cf6',
  },
  {
    title: 'Digital Website - Asst Commissioner Social Welfare',
    department: 'Asst Commissioner Social Welfare Department',
    category: 'Government Portal',
    icon: Globe,
    description: 'Official digital website for Assistant Commissioner Social Welfare Department.',
    color: '#06b6d4',
  },
  {
    title: 'Digital High Tech Library',
    department: 'Regional Deputy Commissioner Office',
    category: 'Education Infrastructure',
    icon: GraduationCap,
    description: 'High-tech digital library at convention centre under Deputy Regional Commissioner Social Welfare Department.',
    color: '#f59e0b',
  },
  {
    title: 'High Tech Barti Platform',
    department: 'Barti',
    category: 'Youth Development',
    icon: Sparkles,
    description: 'Web-based system to assist in planning and implementation of youth group programs for skill development and empowerment.',
    color: '#ec4899',
  },
  {
    title: 'Yuva Gat Youth Programs',
    department: 'Barti',
    category: 'Youth Development',
    icon: Users,
    description: 'Web-based system for youth group program planning and implementation, supporting youth development initiatives.',
    color: '#10b981',
  },
  {
    title: 'Bharat Sports Inter-University',
    department: 'Maharashtra State',
    category: 'Sports Management',
    icon: Award,
    description: 'Digital data management system for inter-university sports across all Maharashtra universities.',
    color: '#6366f1',
  },
  {
    title: 'TB Mukat Bharat Awareness',
    department: 'Government of Maharashtra',
    category: 'Health Awareness',
    icon: Heart,
    description: 'Digital awareness platform for tuberculosis eradication program under Government of Maharashtra, Lokbhavan.',
    color: '#ec4899',
  },
  {
    title: 'Artist Digital Data Management System',
    department: 'South-Central Zone Cultural Department',
    category: 'Cultural Platform',
    icon: Sparkles,
    description: 'Digital data management system for artists, supporting cultural programs and artist database management.',
    color: '#8b5cf6',
  },
  {
    title: 'Digital E-Learning Platform',
    department: 'University of Mumbai',
    category: 'Education Technology',
    icon: GraduationCap,
    description: 'Digital e-learning platform and mobile application for Mumbai city and Mumbai suburban under University of Mumbai.',
    color: '#06b6d4',
  },
]

const stats = [
  { value: '16+', label: 'Projects Delivered' },
  { value: '10+', label: 'Government Departments' },
  { value: '5+', label: 'Districts Covered' },
  { value: '100%', label: 'Success Rate' },
]

export default function ProjectsPage() {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Our Projects | Eonixa Dynamics</title>
        <meta name="description" content="Explore our portfolio of Government Technology, Education, Healthcare and Infrastructure projects across Maharashtra" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
              }}
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">Our Portfolio</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              Our <span className="gradient-text">Projects</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Successfully delivered government projects across Maharashtra. Each project represents our commitment to excellence and innovation.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-8 mt-12"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl font-bold gradient-text">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section ref={ref} className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Government Projects</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Each project is implemented in collaboration with government departments for maximum impact
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((project, index) => {
              const Icon = project.icon
              return (
                <motion.div
                  key={project.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    {/* Category Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-medium px-3 py-1 rounded-full"
                        style={{
                          background: `${project.color}20`,
                          color: project.color,
                          border: `1px solid ${project.color}40`,
                        }}
                      >
                        {project.category}
                      </span>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{
                          background: `${project.color}20`,
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: project.color }} />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                      {project.title}
                    </h3>

                    {/* Department */}
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-400">{project.department}</span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-slate-400 leading-relaxed text-justify">
                      {project.description}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}